import type { Preview } from '../hooks/useDocumentPreview';
import { ErrorBanner } from './ui/ErrorBanner';
import { Spinner } from './ui/Spinner';
import { Icon } from './ui/Icon';

/**
 * Renders a preview state. PDFs go in a frame on the browser's PDF viewer; images inline.
 *
 * The PDF frame must NOT carry `sandbox`: Chromium (Edge, Chrome) refuses to load its PDF viewer in
 * any sandboxed frame ("This page has been blocked") and no sandbox token re-enables it. The blob is
 * forced to `application/pdf` in useDocumentPreview, so the frame can only ever hand bytes to the
 * PDF viewer, never render them as HTML on the app's origin.
 */
export function DocumentPreview({
  preview,
  title,
  height = '70vh',
  compact = false,
}: {
  preview: Preview;
  title: string;
  height?: string;
  compact?: boolean;
}) {
  if (preview.kind === 'loading') {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: compact ? '12rem' : '40vh' }}>
        <Spinner label="Loading preview" size={22} />
      </div>
    );
  }
  if (preview.kind === 'pdf') {
    return (
      <iframe
        title={`${title} (PDF)`}
        src={preview.url}
        referrerPolicy="no-referrer"
        className="w-full border-0 bg-surface-raised"
        style={{ height }}
      />
    );
  }
  if (preview.kind === 'image') {
    return (
      <div className="gsl-preview-frame flex items-center justify-center p-3" style={{ minHeight: compact ? '12rem' : '40vh' }}>
        <img src={preview.url} alt={title} className="max-w-full object-contain" style={{ maxHeight: height }} />
      </div>
    );
  }
  if (preview.kind === 'unsupported') {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center text-sm text-muted-foreground">
        <Icon name="file" size={28} />
        <p>
          No inline preview for this file type. Download it to open in its own application.
        </p>
        <p className="text-xs">{preview.contentType || 'unknown type'}</p>
      </div>
    );
  }
  if (preview.kind === 'unavailable') {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center text-sm text-muted-foreground" role="status">
        <Icon name="info" size={28} />
        <p>{preview.reason}</p>
      </div>
    );
  }
  return (
    <div className="p-4">
      <ErrorBanner title="The preview could not be loaded" error={preview.error} />
    </div>
  );
}
